import React from 'react';
import Image from '../Image';
import { useModal } from '../../context/ModalContext';

const sections = [
  {
    id: 'segredo',
    label: 'O segredo: Personalização Parametrizada',
    image: 'how-custom-first-mosaico.png',
    render: () => (
      <>
        <p>
          Diferente de medicamentos ou hormônios, as doses eficazes de nutrientes e
          compostos naturais não variam drasticamente de pessoa para pessoa. Elas
          operam dentro de faixas bem definidas pela ciência.
        </p>
        <p>
          O que muda na prescrição de nutrientes e ativos são ajustes de contexto
          fisiológico: sexo, idade, peso, funcionamento intestinal, rotina,
          gravidez ou lactância, intensidade de sintomas, objetivos.
        </p>
        <p>
          A ciência já mapeou essas faixas em milhares de estudos e metanálises. O
          que faltava era um sistema inteligente para aplicá-las de forma
          individualizada. É exatamente isso que a Puríssima faz.
        </p>
        <p>
          Na Puríssima, essas faixas não são baseadas apenas em recomendações
          mínimas (RDA), antigas e pensadas apenas para evitar deficiências.
          Trabalhamos com faixas otimizadas, dentro de limites seguros, que
          refletem o que a literatura científica aponta como ideal para
          performance metabólica, longevidade e "prosperidade nutricional", e não
          apenas para "sobreviver sem carência".
        </p>
        <p>
          A personalização por parâmetros, no caso da suplementação, é mais do que
          suficiente para atender às necessidades de mais de 90% das pessoas, e
          pode ser feita de forma simples, rápida e com mínimo custo, apenas
          através de perguntas.
        </p>
        <p>
          Isso permite que o cliente tenha acesso às melhores fórmulas, com doses
          seguras e ideais ajustadas para os parâmetros mais importantes definidos
          pela ciência, através de um ritual agradável, fácil e completo.
        </p>
      </>
    ),
  },
  {
    id: 'tecnologia',
    label: 'TECNOLOGIA QUE SIMPLIFICA',
    image: 'how-custom-anamnese.png',
    side: 'right',
    render: () => (
      <>
        <p>
          Nosso algoritmo, potencializado por inteligência artificial, cruza suas
          informações com parâmetros clínicos universalmente reconhecidos pela
          ciência.
        </p>
        <p>
          Ele identifica padrões, calcula necessidades e sugere a combinação ideal
          de nutrientes, nas doses certas, para seu contexto específico.
        </p>
        <p>
          Em seguida, nossa equipe clínica (nutricionistas e
          farmacêuticos) revisa cada fórmula antes da manipulação.
        </p>
        <p>
          O resultado? <strong>Doses clínicas reais</strong>, comprovadas por
          evidência científica, aplicadas de forma inteligente e personalizada,
          sem depender de exames, mas com total segurança e eficácia.
        </p>
      </>
    ),
  },
  {
    id: 'modelo',
    label: 'O MODELO PURÍSSIMA',
    image: 'terceira-imagem-carroseul.png',
    render: () => (
      <>
        <p>
          Nosso propósito é claro: entregar suplementação completa, de nível
          clínico real, com os melhores ativos e dosagens, a um custo acessível,
          dentro de um ritual agradável e simples.
        </p>
        <p>
          Até então, você tinha duas opções: suplementos genéricos de prateleira
          (subdosados, sem personalização) ou manipulação tradicional com
          prescrição, cara, burocrática e dependente de agenda de profissionais.
        </p>
        <p>
          A Puríssima criou uma terceira via: tecnologia + ciência + manipulação
          farmacêutica de alta qualidade, direto para você, criando um ritual de
          suplementação mais eficiente, agradável e acessível.
        </p>
      </>
    ),
  },
  {
    id: 'nao-fazemos',
    label: 'O QUE NÃO FAZEMOS?',
    image: 'quarta-imagem-carroseul.png',
    render: () => (
      <>
        <p>
          Para nos especializarmos e sermos os melhores no que fazemos, escolhemos
          focar em atender exclusivamente dentro do nosso modelo.
        </p>
        <p>
          Não pegamos receitas de outros profissionais, não substituímos médicos
          nem nutricionistas e não manipulamos medicamentos nem hormônios.
        </p>
        <p>
          Nossa atuação é na base suplementar nutricional: aquilo que é essencial
          para qualquer pessoa que deseja saúde real e sustentada ao longo do
          tempo.
        </p>
        <p>
          Assim, preparamos o terreno para que o médico e o nutricionista possam
          atuar com mais precisão, fazendo os ajustes finos quando necessário.
        </p>
      </>
    ),
  },
];

const ModalSliderCustom = () => {
  const { modalData } = useModal();
  const sectionId = modalData?.sectionId;

  const startIndex = Math.max(
    0,
    sections.findIndex((s) => s.id === sectionId)
  );

  return (
    <div
      id="ModalSliderCustom"
      className="ModalSliderKitFormula carousel slide"
      data-bs-ride="false"
    >
      <div className="carousel-inner">
        {sections.map((section, index) => (
          <div
            key={section.id}
            className={`carousel-item ${index === startIndex ? 'active' : ''}`}
          >
            <div className="container-fluid inner">
              <div className="row justify-content-center text-center">
                <div className="col" style={{ maxWidth: '630px' }}>
                  <h2 className="big">
                    <strong>{section.label}</strong>
                  </h2>
                </div>
              </div>
              <div className="row justify-content-center">
                <div className={section.side === 'right' ? 'col-11 col-lg-4 order-lg-2' : 'col-11 col-lg-4'}>
                  <Image
                    image={section.image}
                    title={section.label}
                    className="img-fluid"
                  />
                </div>
                <div className={section.side === 'right' ? 'col-11 col-lg-8 order-lg-1' : 'col-11 col-lg-8'}>
                  {section.render()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sliderButtons">
        <button
          className="carousel-control-prev"
          type="button"
          data-bs-target="#ModalSliderCustom"
          data-bs-slide="prev"
        >
          <span className="carousel-control-prev-icon" aria-hidden="true" />
          <span className="visually-hidden">Anterior</span>
        </button>
        <button
          className="carousel-control-next"
          type="button"
          data-bs-target="#ModalSliderCustom"
          data-bs-slide="next"
        >
          <span className="carousel-control-next-icon" aria-hidden="true" />
          <span className="visually-hidden">Próximo</span>
        </button>
      </div>
    </div>
  );
};

export default ModalSliderCustom;
